// frontend/src/pages/TransparencyPage.jsx
// 6.C5 + 6.C6 - Public Transparency Page
// "Here's how we're doing" + "Here's what we're improving"

import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, 
  TrendingUp, 
  Users, 
  CheckCircle, 
  Clock, 
  Star,
  ArrowLeft,
  ExternalLink,
  Target,
  Zap,
  Heart,
  MessageSquare,
  BarChart3,
  Truck,
  Coins,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import PlatformTrustScore from '../components/platform/PlatformTrustScore';

// Query for platform accountability metrics
const GET_PLATFORM_ACCOUNTABILITY = gql`
  query PlatformAccountability {
    platformAccountability {
      platformTrustScore
      uptime
      activeUsersThisWeek
      activeUsersThisMonth
      jobCompletionRate
      feedbackByCategory {
        category
        count
        avgRating
      }
    }
  }
`;

// Platform commitments - what we're working on
const PLATFORM_COMMITMENTS = [
  {
    id: 1,
    title: 'Price Transparency',
    description: 'We show you exactly how much you save compared to retail prices. No hidden fees, no surprises.',
    status: 'active',
    icon: Coins,
    color: 'green'
  },
  {
    id: 2,
    title: 'Fair Token System',
    description: 'Our HYBRID token system rewards loyalty while keeping redemption sustainable for everyone.',
    status: 'active',
    icon: Star,
    color: 'yellow'
  },
  {
    id: 3,
    title: 'Community-First Logistics',
    description: 'Members can earn tokens by helping with local deliveries, building a community-powered network.',
    status: 'building',
    icon: Truck,
    color: 'blue'
  },
  {
    id: 4,
    title: 'Vendor Accountability',
    description: 'Vendors are rated by the community. Low performers are removed to maintain quality.',
    status: 'active',
    icon: Shield,
    color: 'purple'
  },
  {
    id: 5,
    title: 'Open Feedback Loop',
    description: "Every piece of feedback impacts our platform score. We can't hide from our users.",
    status: 'active',
    icon: MessageSquare,
    color: 'pink'
  },
  {
    id: 6,
    title: 'Data Privacy',
    description: 'Your data belongs to you. We never sell personal information to third parties.',
    status: 'active',
    icon: Heart,
    color: 'red'
  }
];

// What we're improving
const IMPROVEMENT_AREAS = [
  {
    title: 'Delivery Speed',
    current: '3-5 days',
    target: '1-2 days',
    progress: 40,
    description: 'Expanding our community delivery network'
  },
  {
    title: 'Product Selection',
    current: '49 products',
    target: '200+ products',
    progress: 25,
    description: 'Onboarding more local vendors'
  },
  {
    title: 'Mobile Experience',
    current: 'Web app',
    target: 'Native apps',
    progress: 15,
    description: 'iOS and Android apps in development'
  }
];

const TransparencyPage = () => {
  const navigate = useNavigate();
  
  const { data, loading, error } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    fetchPolicy: 'cache-and-network'
  });

  const metrics = data?.platformAccountability;

  // Category rating display
  const getCategoryIcon = (category) => {
    const icons = {
      checkout: '🛒',
      delivery: '📦',
      products: '⭐',
      app: '📱',
      support: '💬',
      value: '💰',
      general: '📝'
    };
    return icons[category] || '📝';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <Link 
              to="/home" 
              className="text-xl font-bold text-blue-600"
            >
              UniLedge
            </Link>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
              <Shield size={40} />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold mb-4">
            Platform Transparency
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            We believe in radical transparency. Every platform exempts itself from its own rules — 
            but not UniLedge. If we're not earning your trust, you'll see it right here.
          </p>

          {/* Live Trust Score */}
          <div className="inline-block bg-white rounded-2xl p-6 text-gray-900">
            <p className="text-sm text-gray-500 mb-2">Live Platform Trust Score</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-5xl font-bold text-blue-600">
                {loading ? '...' : metrics?.platformTrustScore?.toFixed(1) || '0.0'}
              </span>
              <div className="text-left">
                <span className="text-gray-400 text-lg">/5.0</span>
                <div className="flex gap-0.5 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star} 
                      size={16} 
                      className={star <= Math.round(metrics?.platformTrustScore || 0) 
                        ? 'text-yellow-400 fill-yellow-400' 
                        : 'text-gray-300'
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Based on {metrics?.feedbackByCategory?.reduce((sum, cat) => sum + cat.count, 0) || 0} user ratings
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 space-y-12">
        
        {/* Section 1: Real-Time Metrics */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="text-blue-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">Real-Time Platform Health</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-lg p-4 shadow-sm animate-pulse">
                  <div className="h-8 w-16 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-24 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                icon={<Clock size={20} className="text-green-600" />}
                label="Platform Uptime"
                value={`${((metrics?.uptime || 0) * 100).toFixed(1)}%`}
                subtext="Last 30 days"
                color="green"
              />
              <MetricCard
                icon={<CheckCircle size={20} className="text-blue-600" />}
                label="Job Completion"
                value={`${((metrics?.jobCompletionRate || 0) * 100).toFixed(0)}%`}
                subtext="Community tasks"
                color="blue"
              />
              <MetricCard
                icon={<Users size={20} className="text-purple-600" />}
                label="Active Users"
                value={metrics?.activeUsersThisMonth?.toLocaleString() || '0'}
                subtext="This month"
                color="purple"
              />
              <MetricCard
                icon={<TrendingUp size={20} className="text-orange-600" />}
                label="Weekly Active"
                value={metrics?.activeUsersThisWeek?.toLocaleString() || '0'}
                subtext="This week"
                color="orange"
              />
            </div>
          )}
        </section>

        {/* Section 2: Feedback by Category */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="text-purple-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">User Feedback Breakdown</h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <p className="text-gray-600 mb-6">
              Here's what our users are saying, broken down by category. 
              We can't hide the bad reviews — they're part of our score.
            </p>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                      <div className="h-3 w-full bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : metrics?.feedbackByCategory?.length > 0 ? (
              <div className="space-y-4">
                {metrics.feedbackByCategory.map((cat) => (
                  <div key={cat.category} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">
                      {getCategoryIcon(cat.category)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 capitalize">
                          {cat.category.replace(/_/g, ' ')}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{cat.count} reviews</span>
                          <span className="font-semibold text-gray-900">
                            {cat.avgRating.toFixed(1)}
                          </span>
                          <Star size={14} className="text-yellow-400 fill-yellow-400" />
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-yellow-400 h-2 rounded-full transition-all"
                          style={{ width: `${(cat.avgRating / 5) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare size={40} className="mx-auto mb-3 text-gray-300" />
                <p>No feedback data yet. Be the first to rate us!</p>
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Our Commitments */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Target className="text-green-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">Our Commitments to You</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {PLATFORM_COMMITMENTS.map((commitment) => {
              const Icon = commitment.icon;
              const colorClasses = {
                green: 'bg-green-50 border-green-200 text-green-600',
                yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
                blue: 'bg-blue-50 border-blue-200 text-blue-600',
                purple: 'bg-purple-50 border-purple-200 text-purple-600',
                pink: 'bg-pink-50 border-pink-200 text-pink-600',
                red: 'bg-red-50 border-red-200 text-red-600'
              };
              
              return (
                <div 
                  key={commitment.id}
                  className={`rounded-xl border p-5 ${colorClasses[commitment.color]}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {commitment.title}
                        </h3>
                        {commitment.status === 'active' && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                        {commitment.status === 'building' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Building
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {commitment.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 4: What We're Improving (6.C6) */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Zap className="text-orange-600" size={24} />
            <h2 className="text-2xl font-bold text-gray-900">What We're Improving</h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <p className="text-gray-600 mb-6">
              We're not perfect, and we're not pretending to be. 
              Here's what we're actively working on:
            </p>

            <div className="space-y-6">
              {IMPROVEMENT_AREAS.map((area, index) => (
                <div key={index} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{area.title}</h3>
                    <span className="text-sm text-blue-600 font-medium">
                      {area.progress}% complete
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${area.progress}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Current: </span>
                      <span className="text-gray-900">{area.current}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Target: </span>
                      <span className="text-green-600 font-medium">{area.target}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-2">{area.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5: The Philosophy */}
        <section className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="text-yellow-400" size={24} />
            <h2 className="text-2xl font-bold">Why We Do This</h2>
          </div>
          
          <blockquote className="text-xl italic text-gray-300 mb-6 border-l-4 border-yellow-400 pl-4">
            "Every platform exempts itself from its own rules. UniLedge would be the first platform to say: 
            'We're accountable too.' This is marketing gold and genuine integrity at the same time."
          </blockquote>
          
          <p className="text-gray-400 mb-6">
            Most platforms hide behind corporate speak and legal disclaimers. 
            They rate their users but never let users rate them back. 
            They collect feedback but never show what they do with it.
          </p>
          
          <p className="text-gray-300">
            UniLedge is different. We believe that if we're asking you to trust us with your money and data, 
            you deserve to know exactly how we're performing. No spin, no PR — just the numbers.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link 
              to="/home"
              className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Shopping
            </Link>
            <Link 
              to="/earn"
              className="bg-transparent border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              Learn About Rewards
            </Link>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="text-center py-8">
          <p className="text-gray-600 mb-4">
            Have feedback? Your voice directly impacts our platform score.
          </p>
          <Link 
            to="/support"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <MessageSquare size={18} />
            Contact Support
            <ExternalLink size={14} />
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} UniLedge. Building trust through transparency.
          </p>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <Link to="/home" className="hover:text-white">Home</Link>
            <Link to="/earn" className="hover:text-white">Earn</Link>
            <Link to="/wallet" className="hover:text-white">Wallet</Link>
            <Link to="/support" className="hover:text-white">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, subtext, color }) => {
  const bgColors = {
    green: 'bg-green-50',
    blue: 'bg-blue-50',
    purple: 'bg-purple-50',
    orange: 'bg-orange-50'
  };

  return (
    <div className={`${bgColors[color]} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{subtext}</p>
    </div>
  );
};

export default TransparencyPage;